// src/HomeValuationWidget.js

import widgetTemplate from './hv-widget.html';
import avmTemplate from './avm.html';
import compsTemplate from './comps.html';
import errorTemplate from '../shared/error.html';
import trendsTemplate from '../shared/trends.html';
import * as trends from '../shared/trends';
import * as helpers from '../shared/helpers';
import { bootstrapStyle } from '../shared/styles';
import { library, dom } from '@fortawesome/fontawesome-svg-core'
import { all } from '@awesome.me/kit-bd5fe14630/icons'

class HomeValuationWidget extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        const style = document.createElement('style');
        style.textContent = bootstrapStyle;
        this.shadowRoot.appendChild(style);

        const template = document.createElement('section');
        template.classList.add('ha-widget');
        template.innerHTML = widgetTemplate;
        this.shadowRoot.appendChild(template);

        library.add(...all);
        dom.watch({
            autoReplaceSvgRoot: this.shadowRoot,
            observeMutationsRoot: this.shadowRoot
        });
    }

    connectedCallback() {
        this.baseUrl = helpers.getBaseUrl();
        this.baseApiUrl = helpers.getBaseApiUrl();
        this.primaryColor = this.getAttribute('primary-color') || '#024267';
        this.secondaryColor = this.getAttribute('secondary-color') || '#F79322';
        this.widgetBackground = this.getAttribute('background-color') || 'transparent';
        this.fontFamily = this.getAttribute('font-family') || '"Helvetica Neue", Helvetica, Arial, sans-serif'
        this.fontUrl = this.getAttribute('font-url') || '';
        this.showOverlay = this.getAttribute('show-overlay') || 'no';
        this.primaryErrorText = this.getAttribute('primary-error-text') || 'No automated valuation could be generated for:';
        this.secondaryErrorText = this.getAttribute('secondary-error-text') || 'Want a professional estimate from an agent in the area?';
        this.errorPhone = this.getAttribute('error-phone') || null;
        this.errorEmail = this.getAttribute('error-email') || 'service@homeadvantage.com';
        this.init();
    }

    init() {
        helpers.setColors(this);
        this.setupSearchInput();
    }

    setupSearchInput() {
        const searchInput = this.shadowRoot.getElementById('search-input');
        const searchGroup = this.shadowRoot.getElementById('wrapper');
        const showOverlay = this.showOverlay.toLowerCase() === 'yes';

        if (showOverlay) {
            const overlay = this.shadowRoot.querySelector('.ha-overlay');
            overlay.style.width = '100%';
            overlay.style.padding = '4em';
            overlay.style.backgroundColor = this.getAttribute('overlay-background') || this.primaryColor;

            const overlayHeading = this.getAttribute('overlay-heading');
            if (overlayHeading) {
                const heading = document.createElement('h1');
                heading.classList.add('text-white');
                heading.innerHTML = overlayHeading;
                searchGroup.parentElement.insertBefore(heading, searchGroup);
            }

            const overlaySubHeading = this.getAttribute('overlay-sub-heading');
            if (overlaySubHeading) {
                const subHeading = document.createElement('h6');
                subHeading.classList.add('text-white');;
                subHeading.innerHTML = overlaySubHeading;
                searchGroup.parentElement.insertBefore(subHeading, searchGroup);
            }
        }

        const placeholder = this.getAttribute('text-placeholder') || 'Enter address';
        searchInput.setAttribute('placeholder', placeholder);

        let timeoutId;
        let abortController = new AbortController();

        searchInput.addEventListener('input', (e) => {
            const inputValue = e.target.value.toLowerCase();
            helpers.closeAllLists(this);

            if (inputValue.length < 5) {
                return false;
            }
            const spinner = this.shadowRoot.querySelector('.searching');
            spinner.classList.remove('d-none');

            if (abortController) {
                abortController.abort();
            }
            abortController = new AbortController();

            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                this.getAddress(inputValue, abortController.signal, searchInput);
            }, 300);
        });
    }

    async getAddress(text, signal, searchInput) {
        const endpoint = 'api/services/app/HomeSearch/GetAddressMatch';
        const queryParams = { input: text };
        const url = `${this.baseApiUrl}${endpoint}?${helpers.serializeParams(queryParams)}`;

        try {
            const response = await fetch(url, { signal });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            const autocompleteItems = this.shadowRoot.getElementById('autocomplete-items');
            autocompleteItems.style.display = 'block';

            data.result.matches.forEach(suggestion => {
                const listItem = document.createElement('li');
                listItem.innerHTML = suggestion.label;
                listItem.addEventListener('click', () => {
                    searchInput.value = suggestion.label;
                    helpers.closeAllLists(this);
                    this.getAVM(suggestion.deliveryLine, suggestion.city, suggestion.state);
                });
                autocompleteItems.appendChild(listItem);

                const spinner = this.shadowRoot.querySelector('.searching');
                spinner.classList.add('d-none');
            });
        } catch (error) {
            console.error('Fetch error:', error);
        }
    }

    async getAVM(deliveryLine, city, state) {
        var zipCode = '';
        var hasError = false;

        const errorContainer = this.shadowRoot.querySelector('.error-container');
        errorContainer.classList.add('d-none');
        errorContainer.innerHTML = errorTemplate;

        const loading = this.shadowRoot.querySelector('.loading-overlay').parentElement;
        loading.classList.remove('d-none');
        loading.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const valuationContainer = this.shadowRoot.getElementById("valuationContainer");
        valuationContainer.classList.add('d-none');
        valuationContainer.innerHTML = avmTemplate;

        const statsContainer = this.shadowRoot.getElementById("statsContainer");
        statsContainer.classList.add('d-none');
        statsContainer.innerHTML = trendsTemplate;

        const compContainer = this.shadowRoot.getElementById("compsContainer");
        compContainer.classList.add('d-none');
        compContainer.innerHTML = compsTemplate;

        try {
            const trendsDataEndpoint = 'api/services/app/HomeSearch/GetAVM';
            const trendsQueryParams = { deliveryLine, city, state };
            const trendsUrl = `${this.baseApiUrl}${trendsDataEndpoint}?${helpers.serializeParams(trendsQueryParams)}`;

            const trendsResponse = await fetch(trendsUrl, { headers: helpers.getHeaders() });
            if (!trendsResponse.ok) {
                hasError = true;
                throw new Error('Network response was not ok');
            }

            const avmData = await trendsResponse.json();
            if (!avmData.result) {
                hasError = true;
                throw new Error('AVM had no results');
            }
            if (!hasError) {
                zipCode = avmData.result.address.zip;

                valuationContainer.querySelector('#addressMainTitle').innerHTML = avmData.result.address.deliveryLine;
                valuationContainer.querySelector('#addressSecondTitle').innerHTML = avmData.result.address.city + ', ' + avmData.result.address.state + ' ' + avmData.result.address.zip;
                valuationContainer.querySelector('#bedsValue').innerHTML = avmData.result.attributes.beds;
                valuationContainer.querySelector('#bathsValue').innerHTML = avmData.result.attributes.baths;
                const sqftValueFormatted = new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(avmData.result.attributes.size);
                valuationContainer.querySelector('#sqftValue').innerHTML = sqftValueFormatted;
                const lowValueFormatted = new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(avmData.result.valuations.general.low);
                valuationContainer.querySelector('#lowValue').innerHTML = lowValueFormatted;
                const emvValueFormatted = new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(avmData.result.valuations.general.EMV);
                valuationContainer.querySelector('#emvValue').innerHTML = emvValueFormatted;
                const highValueFormatted = new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                }).format(avmData.result.valuations.general.high);
                valuationContainer.querySelector('#highValue').innerHTML = highValueFormatted;

                compContainer.querySelectorAll('.addressMainTitle').forEach(element => {
                    element.innerHTML = avmData.result.address.deliveryLine;
                });
                compContainer.querySelectorAll('.bedsValue').forEach(element => {
                    element.innerHTML = avmData.result.attributes.beds;
                });
                compContainer.querySelectorAll('.bathsValue').forEach(element => {
                    element.innerHTML = avmData.result.attributes.baths;
                });
                compContainer.querySelectorAll('.sqftValue').forEach(element => {
                    element.innerHTML = sqftValueFormatted;
                });

                let compCount = 0;
                avmData.result.comparables.forEach(data => {
                    this.addTableRow(compContainer, data, compCount);
                    compCount++;
                });
            }
        } catch (error) {
            hasError = true;
            console.error('Fetch error:', error);
        }

        try {
            if (!hasError) {
                const trendsDataEndpoint = 'api/services/app/HomeSearch/GetTrendsData';
                const trendsQueryParams = { search: zipCode };
                const trendsUrl = `${this.baseApiUrl}${trendsDataEndpoint}?${helpers.serializeParams(trendsQueryParams)}`;

                const trendsResponse = await fetch(trendsUrl, { headers: helpers.getHeaders() });
                if (!trendsResponse.ok) {
                    throw new Error('Network response was not ok');
                }

                const trendsData = await trendsResponse.json();
                trends.createMarketTrends(this, trendsData.result.areaTrendStats.trends);
                trends.createPricesAndSalesGraph(this, trendsData.result.areaTrendPricesAndSales.trends);
                trends.createDaysOnMarketGraph(this, trendsData.result.areaTrendsDaysOnMarket.trends);
                trends.createDaysOfInventoryGraph(this, trendsData.result.areaTrendsDaysOfInventory.trends, trendsData.result.areaTrendPricesAndSales.trends);
            }
        } catch (error) {
            hasError = true;
            console.error('Fetch error:', error);
        }

        if (hasError) {
            errorContainer.querySelector('.error-text').innerHTML = this.primaryErrorText + '<br/>' + deliveryLine + ', ' + city + ', ' + state;
            errorContainer.querySelector('.secondary-text').innerHTML = this.secondaryErrorText;

            const contactTemplate = errorContainer.querySelector('.error-contact');
            if (this.errorPhone) {
                const phoneTemplate = document.createElement('div');
                phoneTemplate.classList.add('col', 'text-center');
                phoneTemplate.innerHTML = `<a href="tel:${this.errorPhone}" class="text-primary"><h3>Give us a call</h3><i class="fa-light fa-phone display-3"></i></a>`

                contactTemplate.appendChild(phoneTemplate);
            }
            const emailTemplate = document.createElement('div');
            emailTemplate.classList.add('col', 'text-center');
            emailTemplate.innerHTML = `<a href="mailto:${this.errorEmail}?subject=Help with Home Valuation" class="text-primary"><h3>Send us an e-mail</h3><i class="fa-light fa-envelope display-3"></i></a>`

            contactTemplate.appendChild(emailTemplate);
            errorContainer.classList.remove('d-none');
            errorContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        else {
            valuationContainer.classList.remove('d-none');
            valuationContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            statsContainer.classList.remove('d-none');
            compContainer.classList.remove('d-none');
        }
        loading.classList.add('d-none');
    }

    addTableRow(compContainer, data, i) {
        const tbody = compContainer.querySelector('.table tbody');
        const newRow = document.createElement('tr');

        newRow.innerHTML = `
          <td scope="row">${i + 1}</td>
          <td><span class="addressMainTitle">${data.address.deliveryLine}</span> 
              <span class="d-block d-lg-none">
                <span class="bedsValue">${data.attributes.beds}</span> beds, 
                <span class="bathsValue">${data.attributes.baths}</span> baths, 
                <span class="sqftValue">${data.attributes.size}</span> sqft
              </span>
          </td>
          <td class="d-none d-lg-table-cell"><span class="bedsValue">${data.attributes.beds}</span></td>
          <td class="d-none d-lg-table-cell"><span class="bathsValue">${data.attributes.baths}</span></td>
          <td class="d-none d-lg-table-cell"><span class="sqftValue">${data.attributes.size}</span> sqft</td>
          <td>${new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            style: 'currency',
            currency: 'USD'
        }).format(data.attributes.salePrice)}</td>
          <td class="d-none d-lg-table-cell">${new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(data.attributes.salePrice / data.attributes.size)}</td>
          <td>${this.formatDate(data.attributes.saleDate)}</td>
        `;

        tbody.appendChild(newRow);
    }

    formatDate(unixTimestamp) {
        const date = new Date(unixTimestamp * 1000);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    }
}

if (!customElements.get('home-valuation-widget')) {
    customElements.define('home-valuation-widget', HomeValuationWidget);
}

export default HomeValuationWidget;
